export type SupportedMinAge = 14 | 18;

const parseAccountMinAge = (raw: string | undefined): SupportedMinAge => {
  const parsed = Number.parseInt(raw ?? '18', 10);
  return parsed === 14 ? 14 : 18;
};

const parseExpiryDays = (raw: string | undefined): number => {
  const parsed = Number.parseInt(raw ?? '30', 10);
  if (!Number.isFinite(parsed) || parsed < 1) return 30;
  return parsed;
};

/** Minimum age for self-service rower account creation (14 when minor onboarding enabled). */
export const ACCOUNT_MIN_AGE: SupportedMinAge = parseAccountMinAge(
  process.env.REACT_APP_MIN_ACCOUNT_AGE
);

/** Coach upload attestation wording — unchanged until upload flows support 14+. */
export const UPLOAD_ATTESTATION_MIN_AGE: SupportedMinAge = 18;

/** Days before an unapproved minor onboarding request is expired and removed. */
export const MINOR_CONSENT_EXPIRY_DAYS = parseExpiryDays(process.env.MINOR_CONSENT_EXPIRY_DAYS);

/** When true, parent role appears on RegisterPage and skips org whitelist. */
export const PARENT_SIGNUP_ENABLED = process.env.REACT_APP_PARENT_SIGNUP_ENABLED === 'true';

/** Team-invite minor onboarding age band (independent of self-register ACCOUNT_MIN_AGE). */
export const MINOR_ONBOARDING_MIN_AGE = 14;
export const MINOR_ONBOARDING_ADULT_AGE = 18;

/**
 * GLY-363: days after a parent withdraws consent before the rower is hidden from the
 * coach roster entirely (rather than just blocked/badged). Mirrored in
 * api/lib/consentWithdrawalNotifications.js and the reconsent request email copy
 * (hardcoded there, defaulting to the same 7 days) since serverless functions can't
 * import this client config module.
 */
export const PARENTAL_CONSENT_ROSTER_HIDE_DAYS = 7;

export function calculateAgeFromBirthDate(birthDate: string): number | null {
  const dob = new Date(birthDate);
  if (Number.isNaN(dob.getTime())) {
    return null;
  }
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDelta = today.getMonth() - dob.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age;
}

/** True when birth date qualifies for team-invite minor parent-consent flow (14–17). */
export function isTeamInviteMinorAge(birthDate: string): boolean {
  const age = calculateAgeFromBirthDate(birthDate);
  if (age == null) return false;
  return age >= MINOR_ONBOARDING_MIN_AGE && age < MINOR_ONBOARDING_ADULT_AGE;
}
